<template>
  <div class="page-container">
    <PageHeader title="部门管理">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd(null)">新增部门</el-button>
      </template>
    </PageHeader>

    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="tableData"
        :columns="columns"
        row-id-key="adId"
        is-tree-table
        tree-children-key="children"
        default-expand-all
        @operate-btn-click="handleOperateBtnClick"
      />
      <EmptyState v-if="!loading && tableData.length === 0" title="暂无部门" icon="el-icon-office-building"></EmptyState>
    </TablePanel>

    <aj-form-add
      :dialog-visible.sync="addDialogVisible"
      :form-config="deptFormConfig"
      :form-data="addFormData"
      dialog-title="新增部门"
      dialog-width="45%"
      :confirm-loading="submitLoading"
      @confirm="handleAddSubmit"
    />

    <aj-form-edit
      :dialog-visible.sync="editDialogVisible"
      :form-config="deptFormConfig"
      :form-data="editFormData"
      dialog-title="编辑部门"
      dialog-width="45%"
      :confirm-loading="submitLoading"
      @confirm="handleEditSubmit"
    />
  </div>
</template>

<script>
import { getDepartmentList, addDepartment, updateDepartment, deleteDepartment } from '@/api/system'
import AjFormAdd from '@/components/ajForm/ajFormAdd.vue'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import TablePanel from '@/components/TablePanel.vue'

const DEPT_COLUMNS = [
  { prop: 'adName', label: '部门名称', minWidth: 200 },
  { prop: 'adOrder', label: '排序', width: 80 },
  { prop: 'createusername', label: '创建人', width: 100 },
  { prop: 'createtime', label: '创建时间', width: 160 },
  {
    type: 'operation',
    prop: 'operation',
    label: '操作',
    width: 200,
    fixed: 'right',
    buttons: [
      { key: 'addChild', text: '新增', type: 'text' },
      { key: 'edit', text: '编辑', type: 'text' },
      { key: 'delete', text: '删除', type: 'text' }
    ]
  }
]

const DEPT_CASCADER_PROPS = {
  checkStrictly: true,
  value: 'adId',
  label: 'adName',
  children: 'children',
  emitPath: false
}

function createEmptyDeptFormData () {
  return { adId: null, adParentId: null, adName: '', adOrder: 0, parentPath: null }
}

export default {
  name: 'SystemDeptPage',
  components: { AjFormAdd, AjFormEdit, AjTable, EmptyState, PageHeader, TablePanel },
  data () {
    return {
      loading: false,
      submitLoading: false,
      columns: DEPT_COLUMNS,
      tableData: [],
      deptTreeOptions: [],
      addDialogVisible: false,
      editDialogVisible: false,
      addFormData: createEmptyDeptFormData(),
      editFormData: createEmptyDeptFormData()
    }
  },
  computed: {
    deptFormConfig () {
      return [
        [
          {
            label: '上级部门',
            prop: 'parentPath',
            type: 'cascader',
            span: 24,
            options: this.deptTreeOptions,
            props: DEPT_CASCADER_PROPS,
            clearable: true,
            placeholder: '不选则为顶级部门'
          }
        ],
        [
          { label: '部门名称', prop: 'adName', type: 'input', span: 24, required: true },
          { label: '排序', prop: 'adOrder', type: 'inputNumber', span: 24, min: 0, max: 999 }
        ]
      ]
    }
  },
  created () { this.fetchList() },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const res = await getDepartmentList()
        this.tableData = Array.isArray(res.data) ? res.data : []
        this.deptTreeOptions = this.buildTreeOptions(this.tableData)
      } catch (error) {
        this.tableData = []
        this.deptTreeOptions = []
      } finally {
        this.loading = false
      }
    },
    buildTreeOptions (list) {
      return list.map(item => ({
        adId: item.adId,
        adName: item.adName,
        children: item.children && item.children.length > 0 ? this.buildTreeOptions(item.children) : []
      }))
    },
    handleAdd (row) {
      this.addFormData = {
        adId: null,
        adParentId: row ? row.adId : null,
        adName: '',
        adOrder: 0,
        parentPath: row ? row.adId : null
      }
      this.addDialogVisible = true
    },
    handleEdit (row) {
      this.editFormData = {
        adId: row.adId,
        adParentId: row.adParentId || null,
        adName: row.adName,
        adOrder: row.adOrder || 0,
        parentPath: row.adParentId || null
      }
      this.editDialogVisible = true
    },
    buildDeptPayload (formData) {
      return {
        adId: formData.adId,
        adParentId: formData.parentPath || null,
        adName: formData.adName,
        adOrder: formData.adOrder
      }
    },
    async handleAddSubmit ({ data }) {
      this.submitLoading = true
      try {
        await addDepartment(this.buildDeptPayload(data))
        this.$message.success('部门创建成功')
        this.addDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },
    async handleEditSubmit ({ data }) {
      this.submitLoading = true
      try {
        await updateDepartment(this.buildDeptPayload(data))
        this.$message.success('部门更新成功')
        this.editDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },
    async handleDelete (row) {
      if (row.children && row.children.length > 0) {
        this.$message.warning('请先删除子部门')
        return
      }
      try {
        await this.$confirm('确认删除该部门吗？', '提示', { type: 'warning' })
        await deleteDepartment({ adId: row.adId })
        this.$message.success('部门已删除')
        this.fetchList()
      } catch (error) {}
    },
    handleOperateBtnClick ({ btn, row }) {
      switch (btn.key) {
        case 'addChild': this.handleAdd(row); break
        case 'edit': this.handleEdit(row); break
        case 'delete': this.handleDelete(row); break
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

::v-deep .aj-table-operation-link {
  margin: 0 6px;
}
</style>
